import { Box, Button, Flex, Heading, HStack, Spacer, Table, Tbody, Td, Text, Th, Thead, Tooltip, Tr, useDisclosure } from '@chakra-ui/react';
import { useState } from 'react';
import { useDispatch } from 'react-redux';
import { BsPencilSquare, BsPlus, BsTrash } from 'react-icons/bs';
import { useNavigate } from 'react-router-dom';
import { useGetFormsQuery, useDeleteFormMutation } from '@/app/services/nucleus';
import { setNotificationLogMessage } from '@/features/notification/notificationSlice';
import ModalComponent from '@/features/common/ModalComponent';
import AlertComponent from '@/features/common/AlertComponent';
import TableButton from '@/features/common/table/TableButton';
import LoaderOverlay from '@/features/common/LoaderOverlay';

const FormsPage = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { isOpen: isDeleteOpen, onOpen: onDeleteOpen, onClose: onDeleteClose } = useDisclosure();
  const [pendingDelete, setPendingDelete] = useState(null);

  const { data: formsData, isLoading, isError, error } = useGetFormsQuery({ limit: 100, offset: 0 });
  const [deleteForm, { isError: isDeleteError, error: deleteError, reset: resetDelete }] = useDeleteFormMutation();

  const forms = formsData?.items ?? [];

  const handleEdit = (slug) => navigate(`/forms/${slug}`);

  const handleDelete = (slug, name) => {
    setPendingDelete({ slug, name });
    onDeleteOpen();
  };

  const handleConfirmDelete = () => {
    if (!pendingDelete) return;
    resetDelete();
    onDeleteClose();
    deleteForm(pendingDelete.slug)
      .unwrap()
      .then(() => dispatch(setNotificationLogMessage({ msg: 'Form deleted successfully', status: 'success' })))
      .catch(() => {});
  };

  return (
    <Box data-test-id="pact-forms-page" position="relative">
      <LoaderOverlay loading={isLoading} />
      <Flex alignItems="center" mb={6}>
        <Heading as="h4" size="md" fontWeight="bold" color="brand.900">
          Form Builder
        </Heading>
        <Spacer />
        <Button leftIcon={<BsPlus />} onClick={() => navigate('/forms/new')} variant="purple">
          New Form
        </Button>
      </Flex>
      {isError && <AlertComponent status="warning" title="Error fetching forms" description={error?.data?.detail ?? 'An error occurred'} />}
      {isDeleteError && <AlertComponent status="error" title="Error deleting form" description={deleteError?.data?.detail ?? 'An error occurred'} />}
      {!isLoading && forms.length === 0 && !isError && (
        <Text color="gray.500">No forms configured yet. Click &quot;New Form&quot; to get started.</Text>
      )}
      {forms.length > 0 && (
        <Table variant="simple">
          <Thead>
            <Tr>
              <Th>Name</Th>
              <Th>Slug</Th>
              <Th>Fields</Th>
              <Th>Action</Th>
            </Tr>
          </Thead>
          <Tbody>
            {forms.map((form) => (
              <Tr key={form.slug}>
                <Td fontWeight="medium">{form.name}</Td>
                <Td color="gray.500" fontFamily="mono" fontSize="sm">
                  {form.slug}
                </Td>
                <Td>{form.fields?.length ?? 0}</Td>
                <Td>
                  <HStack spacing={1}>
                    <Tooltip hasArrow placement="top" label="Edit Form">
                      <Box p="0" as="span">
                        <TableButton aria-label="edit" icon={<BsPencilSquare />} onClick={() => handleEdit(form.slug)} />
                      </Box>
                    </Tooltip>
                    <Tooltip hasArrow placement="top" label="Delete Form">
                      <Box p="0" as="span">
                        <TableButton aria-label="delete" icon={<BsTrash />} onClick={() => handleDelete(form.slug, form.name)} />
                      </Box>
                    </Tooltip>
                  </HStack>
                </Td>
              </Tr>
            ))}
          </Tbody>
        </Table>
      )}
      <ModalComponent
        size="sm"
        title="Delete Form"
        primaryText="Yes"
        secondaryText="No"
        handleConfirm={handleConfirmDelete}
        isOpen={isDeleteOpen}
        onClose={onDeleteClose}
        isError={false}
      >
        <Text>
          Are you sure you want to delete <strong>{pendingDelete?.name}</strong>? This action is non-recoverable.
        </Text>
      </ModalComponent>
    </Box>
  );
};

export default FormsPage;
